import { findAssetIds } from '@amzn/amazon-journeys-utils/lib/util/JourneysUtil';
import {
  type Language,
  MARKETPLACE_TO_MARKETPLACE_AMAZON,
  type Marketplace,
  marketplaceToConfig,
  PLATFORMS,
  type SharedVarsMap,
} from '@amzn/journeys-common';
import { capitalize, trim, uniq } from 'es-toolkit';
import { getLanguageDisplayFromLanguage } from 'features/campaign/campaign.util';
import type { TrafficCampaignFlavor } from 'generated/graphql';
import { getConfigDrivenFormFilteredAssets } from '../campaign-types/config-driven/publishing/get-filtered-assets';
import type { MarsCgContent } from '../content/below-the-fold/mars-grid.types';

// Types for interactions with RAW (Remotely Accessible Widget) APIs
export type MarsContent = MarsCgContent;

export type PreviewSession = {
  content: MarsContent;
};

// https://code.amazon.com/packages/MarsMarinerServiceClient/blobs/mainline-2.0/--/src/com/amazon/mars/mariner/service/client/model/MarsWidgetDefinition.java
export type MarsWidgetDefinition = {
  widgetName: string;
  widgetVersion: string;
  arguments: {
    // biome-ignore lint/suspicious/noExplicitAny: legacy code
    [key: string]: any;
  };
  pageType?: string;
  subPageType?: string;
  userAgent?: string;
  weblabs?: {
    [key: string]: string;
  };
  customerID?: string;
  marketplaceID?: string;
  sessionID?: string;
  request?: {
    // biome-ignore lint/suspicious/noExplicitAny: legacy code
    [key: string]: any;
  };
  isInternal?: boolean;
  uri?: string;
  parameters?: {
    [key: string]: string;
  };
};

// The <html> tag on detail pages have different classes that control styles applied. These were taken
// from a live Detail Page.
export function getHtmlClass(platform: PLATFORMS): string {
  if (platform === PLATFORMS.TABLET) {
    return (
      'a-touch a-tablet a-js a-audio a-video a-canvas a-svg a-drag-drop ' +
      'a-geolocation a-history a-webworker a-autofocus a-input-placeholder ' +
      'a-textarea-placeholder a-local-storage a-gradients a-transform3d ' +
      'a-touch-scrolling a-tablet a-text-shadow a-text-stroke a-box-shadow ' +
      'a-border-radius a-border-image a-opacity a-transform a-transition a-ember'
    );
  }
  if (platform === PLATFORMS.MOBILE) {
    return (
      'a-touch a-mobile a-js a-audio a-video a-canvas a-svg a-drag-drop ' +
      'a-geolocation a-history a-webworker a-autofocus a-input-placeholder ' +
      'a-textarea-placeholder a-local-storage a-gradients a-transform3d ' +
      'a-touch-scrolling a-text-shadow a-text-stroke a-box-shadow ' +
      'a-border-radius a-border-image a-opacity a-transform a-transition a-ember awa-browser'
    );
  }
  // DESKTOP
  return (
    'a-ws a-js a-audio a-video a-canvas a-svg a-drag-drop a-geolocation ' +
    'a-history a-webworker a-autofocus a-input-placeholder a-textarea-placeholder ' +
    'a-local-storage a-gradients a-transform3d -scrolling a-text-shadow a-text-stroke ' +
    'a-box-shadow a-border-radius a-border-image a-opacity a-transform a-transition a-ember'
  );
}

export function getMarketplaceClass(marketplace: Marketplace): string {
  if (marketplace === 'JP') {
    return 'marketplace-jp';
  }
  return 'marketplace-default';
}

export function getPlatformClass(platform: PLATFORMS): string {
  return `platform-${platform?.toLowerCase()}`;
}

/**
 * Sanitize the HTML for the live preview modal
 * @param widgetHtml
 * @param keepProvidedStyles whether to keep any embedded styles in the widget HTML
 * @param keepProvidedScripts whether to keep any embedded scripts
 */
export function sanitizeWidgetHtml({
  widgetHtml,
  keepProvidedStyles,
  keepProvidedScripts,
}: {
  widgetHtml: string;
  keepProvidedStyles: boolean;
  keepProvidedScripts: boolean;
}) {
  const localDom = new DOMParser().parseFromString(`<html>${widgetHtml}</html>`, 'text/html');
  if (!keepProvidedStyles)
    // remove any embedded styles in the widget HTML, as we're including content grid styles already, and the
    // embedded styles will not be for the correct variant. They will not be for the correct variant because
    // unlike Amabot we don't pass in customer metadata with the MaRS request. It is possible for us to do this,
    // however, by adding parameters to the request (any in the MarsWidgetDefinition interface, e.g. userAgent).
    localDom.querySelectorAll("link[rel='stylesheet']").forEach((value) => value.remove());

  if (!keepProvidedScripts)
    // remove any embedded scripts, as these will throw errors that they can't find "P" (i.e. AUI JS)
    localDom.querySelectorAll('script').forEach((value) => value.remove());

  return localDom.documentElement.innerHTML;
}

/**
 * Retrieves a unique list of asset IDs from a given traffic campaign flavor.
 *
 * @param {Object} params - The parameters for the function.
 * @param {TrafficCampaignFlavor} params.flavor - The traffic campaign flavor object containing the content.
 * @param {SharedVarsMap} params.sharedVarsMap - The shared variables map used to find asset IDs.
 * @returns {string[]} - A unique array of asset IDs combined from findAssetIds and getConfigDrivenFormFilteredAssets.
 * @throws {Error} - If any of the asset IDs within the `jsonText` property of the `content` object do not exist in the `campaignAssets` array of the `globalContent` object.
 */
export function getAssetIdsInFlavor({ flavor, sharedVarsMap }: { flavor: TrafficCampaignFlavor; sharedVarsMap: SharedVarsMap }): string[] {
  const assetIds = findAssetIds({
    flavorContent: flavor.content,
    sharedVarsMap,
  });
  const assetIdsInConfigDrivenFrom = getConfigDrivenFormFilteredAssets(flavor)
    .filter((asset) => asset != null)
    .map((asset) => asset.assetId);
  return uniq([
    ...assetIds,
    ...assetIdsInConfigDrivenFrom,
  ]);
}

/**
 * MaRS encodes video block URLs with OWASP's java encoding library to prevent XSS. This is a simple util to decode the strings.
 * @param str - The encoded string
 * @returns - A decoded string
 */
export const decodeOWASPJavaScriptURL = (str: string) => str.replaceAll(/\\x26/g, '&').replaceAll(/\\/g, '');

/**
 * Returns true if a string is an absolute URL
 * - ex: '/dp/B123412345', '/foo/bar/baz'
 * @example
 * ```
 *
 * ```
 */
const isAbsoluteUrl = (url: string): boolean => {
  try {
    // Attempt to create a new URL object
    new URL(url);
    return true; // If no error is thrown, it's an absolute URL
  } catch {
    return false; // If an error is thrown, it's a relative URL
  }
};

/**
 * Given a widget's HTML, make all links clickable
 * - If the link is absolute, open in a new tab
 * - If the link is a anchor link ("#some-id-on-page"), do nothing
 * - If the link is relative, make it absolute and open in a new tab
 * @param localDom - the dom to manipulate
 * @param marketplace - used to construct the absolute URL for relative paths
 */
const makeLinksClickable = ({ localDom, marketplace }: { localDom: Document; marketplace: Marketplace }): void => {
  const marketplaceAmazon = MARKETPLACE_TO_MARKETPLACE_AMAZON[marketplace];
  const marketplaceURLBase = `https://${marketplaceToConfig[marketplaceAmazon].siteName}`;
  localDom.querySelectorAll('a').forEach((link) => {
    // First get rid of any clicking side effects
    // Make all links non-ops, so that we don't load a mini Journeys inside of the preview modal
    link.setAttribute('onclick', 'return false;');

    // Important, do not change to link.href because link.getAttribute('href') !== link.href
    const currentHref = link.getAttribute('href');
    if (currentHref === null) return;
    if (currentHref === '') return;
    if (currentHref.startsWith('#')) {
      // Anchor links are used for nav and we handle that elsewhere
      return;
    }
    if (
      currentHref.startsWith('javascript:') ||
      currentHref.startsWith('tel:') ||
      currentHref.startsWith('mailto:') ||
      currentHref.startsWith('sms:')
    ) {
      // Highly unlikely to come across these...
      return;
    }

    if (isAbsoluteUrl(currentHref)) {
      // If the link is already absolute, we don't need to do anything
      link.setAttribute('target', '_blank');
      return;
    }

    try {
      // currentHref is likely either of the following:
      // - root-relative URL: `/dp/B123456789`
      //   - used commonly in tech specs
      // - relative url like `dp/B123456789`,
      //   - almost never used in our campaigns
      //   - treated like root-relative for preview because we don't know exactly where
      //     this content will be rendered on the retail site.

      // Combine the currentHref value with the marketplace specific base URL
      const newHrefUsingURLConstructor = new URL(currentHref, marketplaceURLBase);
      link.setAttribute('href', newHrefUsingURLConstructor.href);
      link.setAttribute('target', '_blank');
    } catch (e) {
      // could not form URL given user input
      console.error(e);

      if (e instanceof TypeError) {
        // This will be displayed as an alert, so we make the message more helpful
        throw new Error(`Problem generating true URL where base = "${marketplaceURLBase}" and path = "${currentHref}"`);
      }
    }
  });
};

/**
 * Given a widget's HTML, an array of placeholders used in the widget, and a mapping of asset IDs,
 * insert all of the correct images into the widget by replacing the placeholders.
 *
 * @param widgetHtml - Stringified div element containing the widget
 * @param s3Urls - List of s3 URLs used in this "live preview session"
 * @returns {string} - corrected HTML
 */
export function replaceMARSContent({
  widgetHtml,
  s3Urls,
  marketplace,
}: {
  widgetHtml: string;
  s3Urls: string[];
  marketplace: Marketplace;
}): string {
  const hiresSrcAttr = 'data-a-hires';
  const localDom = new DOMParser().parseFromString(`<html>${widgetHtml}</html>`, 'text/html');

  // If the HTML comes back with real media central URLs, make sure to lazily load in their assets.
  const imgElementsWithRealUrls = localDom.querySelectorAll(`img[src*="data:image"][${hiresSrcAttr}*="m.media"]`);
  imgElementsWithRealUrls.forEach((elem) => elem.setAttribute('src', elem.getAttribute(hiresSrcAttr)!));

  // replace MaRS's lazy video asset loader scripts with video elements
  // we don't have access to the real MaRS scripts, so here we'll create
  // 1 video element per video script and fill in the real URLs later
  localDom.querySelectorAll('div[id*="cg-video-container"]').forEach((block) => {
    const videoScriptElement = block?.parentElement?.querySelector('script');

    // in the HTML from MaRS, the video script is plaintext, but it's HTML in unit tests.
    // we use nullish coalescing here to be compatible with both.
    const videoScript = videoScriptElement?.innerText ?? videoScriptElement?.innerHTML;

    const videoUrl: string = videoScript?.match(/(\sURL|videoUrl): ["']([^\n]+)["']/)?.[2] as string;
    const posterUrl: string = videoScript?.match(/(imageUrl): ["']([^\n]+)["']/)?.[2] as string;

    const placeholderVid = document.createElement('video');
    placeholderVid.setAttribute('class', 'content-grid-cinemagraph-placeholder');
    placeholderVid.setAttribute('src', decodeOWASPJavaScriptURL(videoUrl!));
    placeholderVid.setAttribute('controls', 'true');

    posterUrl && placeholderVid.setAttribute('poster', decodeOWASPJavaScriptURL(posterUrl!));
    block.appendChild(placeholderVid);
  });

  s3Urls.forEach((s3Url) => {
    localDom.querySelectorAll(`video[src*="${s3Url}"]`).forEach((elem) => elem.setAttribute('src', s3Url));
    localDom.querySelectorAll(`video[poster*="${s3Url}"]`).forEach((elem) => elem.setAttribute('poster', s3Url));

    // Animations or cinemagraphs keep a placeholder image, but it isn't visible by default.
    // If we change the "poster" attribute of the cinemagraph object, it will display the preview.
    localDom
      .querySelectorAll('video[id*="ods-cg-cinemagraph"]')
      .forEach((block) => block.setAttribute('src', block.getAttribute('data-src')!));
  });

  makeLinksClickable({
    localDom,
    marketplace,
  });

  return localDom.documentElement.innerHTML;
}

/**
 * Taken from https://code.amazon.com/packages/MarsCompatibilityMessageService/blobs/d6b4019fade0d45027ccd39185954e378bf670f2/--/.storybook/preview-head.html#L6-L9
 * Eventually we should figure out a more deterministic way to pull this.
 */
const auiCss =
  '<link rel="stylesheet" href="https://images-na.ssl-images-amazon.com/images/I/11EIQ5IGqaL._RC|01ZTHTZObnL.css,41wZkyTaWoL.css,31Y8m1dzTdL.css,013z33uKh2L.css,017DsKjNQJL.css,0131vqwP5UL.css,41EWOOlBJ9L.css,11TIuySqr6L.css,01ElnPiDxWL.css,11bGSgD5pDL.css,01Dm5eKVxwL.css,01IdKcBuAdL.css,01y-XAlI+2L.css,21N4kUH7pxL.css,01oDR3IULNL.css,41CYNGpGlrL.css,01XPHJk60-L.css,114y0SIP+yL.css,21aPhFy+riL.css,11gneA3MtJL.css,21fecG8pUzL.css,01ulGzBW88L.css,01CFUgsA-YL.css,31C80IiXalL.css,11qour3ND0L.css,11gKCCKQV+L.css,11061HxnEvL.css,11oHt2HYxnL.css,013RDhw9hoL.css,11JQtnL-6eL.css,116v6uYvN6L.css,11jtXRmppwL.css,01QrWuRrZ-L.css,21zuRztKjtL.css,11QyqG8yiqL.css,11K24eOJg4L.css,11F2+OBzLyL.css,01890+Vwk8L.css,11Y05DTEL6L.css,01cbS3UK11L.css,21F85am0yFL.css,01giMEP+djL.css_.css?AUIClients/AmazonUI#us.not-trident" />';

/**
 * Taken from https://code.amazon.com/packages/MarsCompatibilityMessageService/blobs/d6b4019fade0d45027ccd39185954e378bf670f2/--/.storybook/preview-head.html#L12
 * Eventually we should figure out a more deterministic way to pull this.
 */
const initialJsLoad =
  '<script src="https://images-na.ssl-images-amazon.com/images/I/0107kh7+MnL._RC%7C61855cYdG+L.js_.js#AUIClients/AmazonUIPageJS"></script>';

/**
 * Taken from https://code.amazon.com/packages/MarsCompatibilityMessageService/blobs/d6b4019fade0d45027ccd39185954e378bf670f2/--/.storybook/preview-head.html#L13-L17
 * Eventually we should figure out a more deterministic way to pull this.
 */
const additionalJsLoad =
  '<script type="text/javascript">P.load.js("https://images-na.ssl-images-amazon.com/images/I/81REOJowviL._RC%7C311O4vp6T1L.js,81DBxlGfLLL.js,31Nvzm6hv7L.js,31mQF4pQYzL.js,11gB9T-veDL.js,21t2AdNUGjL.js,31FxSUkeU1L.js,31mQpikWRCL.js,71GB6lyYUeL.js,31Wv8c+g8yL.js,01CneC-odkL.js,21-7w3nUjfL.js,31Ci93aByhL.js,41DCXoSvywL.js,21bBU3C02WL.js,71aPJyL448L.js,51iMSb3sCyL.js,21nJ9Kc79pL.js,01neGyqrxaL.js,41nyNLq0r1L.js,21zkY9xrxvL.js,51q3g6xpLTL.js,01DvxIlz14L.js,01aZtTr83cL.js_.js#AUIClients/AmazonUI")</script>';
/**
 * We need to tell our live preview iFrame that we are in an above-the-fold context.
 * https://console.harmony.a2z.com/aui/v3/development/js-framework/page-support/module-system/#P.when
 */
const aboveTheFoldFlag = '<script type="text/javascript">P.when("A", "ready").register("af");P.when("A", "ready").register("cf");</script>';

/**
 * If a mars widget depends on AmazonUI JS/CSS, adding this into the iFrame for the LivePreview will allow
 */
export const auiHtml = `
${auiCss}
${initialJsLoad}
${additionalJsLoad}
${aboveTheFoldFlag}
`;

/**
 * Generates a flavor label for the live preview language selector
 * @param flavor - The traffic campaign flavor
 * @param showPlatformDropdown - Whether a separate platform dropdown is shown
 * @returns The formatted flavor label
 */
export const generateFlavorLabel = (flavor: TrafficCampaignFlavor, showPlatformDropdown: boolean): string => {
  const marketplace = flavor.scheduling.marketplace;
  const language = getLanguageDisplayFromLanguage(flavor.scheduling.language as Language) ?? '';
  const platform = capitalize(flavor.scheduling.platform);
  const locale = `${marketplace} ${language}`;
  const flavorLabel = showPlatformDropdown ? locale : `${locale} ${platform}`;

  return trim(flavorLabel);
};

/**
 * Generates the base marketplace URL for a given marketplace identifier.
 * @param {Marketplace} marketplace - The marketplace identifier (e.g., 'US', 'DE', 'JP', etc.)
 * @returns {string} The complete HTTPS URL for the marketplace.
 */
export const getMarketplaceUrl = (marketplace: Marketplace): string => {
  const marketplaceAmazon = MARKETPLACE_TO_MARKETPLACE_AMAZON[marketplace];
  return `https://${marketplaceToConfig[marketplaceAmazon].siteName}`;
};
